import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, Check, MessageCircle } from 'lucide-react';
import { SEO } from '../components/common';
import { Button, ServiceCard } from '../components/ui';
import { SERVICES, CONTACT_INFO } from '../utils/constants';

/**
 * Services page component
 */
const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState(null);

  const handleServiceClick = (service) => {
    setSelectedService(service);
  };

  const closeModal = () => {
    setSelectedService(null);
  };

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/services' },
  ];

  return (
    <>
      <SEO
        title="MRI Services - Open MRI, Whole Body MRI, Angiogram"
        description="Comprehensive MRI services at Mian Tufail Diagnostic Center Kasur: Siemens Magnetom C2 Open MRI, Whole Body MRI, MR Angiogram, MR Venogram, Brain MRI, Spine MRI, Joint MRI. Advanced imaging technology."
        keywords="MRI services Kasur, Open MRI Kasur, whole body MRI scan, MR angiogram Pakistan, MR venogram, brain MRI scan, spine MRI, joint MRI, Siemens MRI, diagnostic imaging services, medical imaging Kasur"
        url="/services"
        breadcrumbs={breadcrumbs}
        services={SERVICES}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Comprehensive <span className="gradient-text">MRI Services</span>
            </h1>
            <p className="text-lg text-neutral-600 leading-relaxed">
              We provide a comprehensive range of diagnostic services with precision and care, 
              using the latest Siemens technology for accurate results.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-neutral-900 mb-8">Featured Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.filter((s) => s.featured).map((service) => (
              <ServiceCard
                key={service.id}
                title={service.title}
                description={service.shortDescription}
                icon={service.icon}
                featured={service.featured}
                onClick={() => handleServiceClick(service)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* All Services */}
      <section className="py-16 bg-neutral-50">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-neutral-900 mb-8">All Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceClick(service)}
                className="bg-white rounded-xl p-6 text-left border border-neutral-100 hover:border-primary-200 hover:shadow-soft transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
                  <service.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-neutral-500">{service.shortDescription}</p>
                <span className="inline-flex items-center text-primary-600 text-sm font-medium mt-3 group-hover:gap-2 transition-all">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-700">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Need a Specific MRI Service?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            Contact us to discuss your diagnostic needs. Our expert team will help you 
            choose the right service for your requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-appointment">
              <Button variant="accent" size="lg" icon={ArrowRight} iconPosition="right">
                Book Appointment
              </Button>
            </Link>
            <a
              href={CONTACT_INFO.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="secondary" size="lg" icon={MessageCircle}>
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      {selectedService && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-modal-title"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Modal Header */}
            <div className="p-8 pb-0">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <selectedService.icon className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <h2 id="service-modal-title" className="text-2xl font-bold text-neutral-900">
                    {selectedService.title}
                  </h2>
                  <p className="text-neutral-500">{selectedService.shortTitle}</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8">
              <p className="text-neutral-600 leading-relaxed mb-6">
                {selectedService.description}
              </p>

              {/* Details List */}
              <h3 className="font-semibold text-neutral-900 mb-4">Key Features</h3>
              <ul className="space-y-3 mb-8">
                {selectedService.details.map((detail, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-600">{detail}</span>
                  </li>
                ))}
              </ul>

              {/* Actions */}
              <div className="flex flex-wrap gap-4">
                <Link to="/book-appointment" onClick={closeModal}>
                  <Button variant="primary" icon={ArrowRight} iconPosition="right">
                    Book This Service
                  </Button>
                </Link>
                <a
                  href={CONTACT_INFO.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" icon={MessageCircle}>
                    Ask a Question
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServicesPage;
